/**
 * Comparateur de deux versions d'une application (§3.5).
 *
 * Deux sélecteurs, un basculement entre code transformé et code original, et le
 * comparatif proprement dit délégué à `CodeDiff`. Le composant ne calcule
 * aucune différence : il choisit quoi comparer et affiche ce que l'API renvoie.
 *
 * Le sens de la comparaison est toujours celui de la réponse : l'API place la
 * plus ancienne des deux versions dans `base` et la plus récente dans `target`,
 * quel que soit l'ordre dans lequel l'utilisateur les a choisies. Les libellés
 * sont donc repris de la réponse et non des sélecteurs.
 */

import type { AppVersionDto, VersionDiffDto } from '@portail/shared';
import { useId, useState } from 'react';

import { getVersionDiff } from '../api';
import { useAsync } from '../hooks/useAsync';
import { defaultComparisonVersionId } from '../lib/diff';
import { ArtifactPreview } from './ArtifactPreview';
import { CodeDiff } from './CodeDiff';
import { Button, EmptyState, ErrorBanner, Label, Select, Skeleton } from './ui';
import { ViewSwitch, type ComparisonView } from './ViewSwitch';
import { versionLabel } from './VersionHistory';

/** Face du code comparée : celle produite par l'IA, ou celle collée par l'auteur. */
type CodeSource = 'transformed' | 'original';

/** Propriétés du comparateur. */
export interface VersionDiffPanelProps {
  appId: string;
  /** Versions de l'application, du plus récent au plus ancien. */
  versions: AppVersionDto[];
  /** Version comparée, choisie depuis l'historique. */
  targetVersionId: string | null;
  onTargetVersionChange: (versionId: string) => void;
}

/** Sélecteurs de versions et comparatif du code. */
export function VersionDiffPanel({
  appId,
  versions,
  targetVersionId,
  onTargetVersionChange,
}: VersionDiffPanelProps) {
  const baseSelectId = useId();
  const targetSelectId = useId();
  const [source, setSource] = useState<CodeSource>('transformed');
  /**
   * L'aperçu visuel est proposé **en premier** : c'est la question que se pose
   * l'auteur d'une application, et le comparatif de code ne lui parle pas.
   */
  const [view, setView] = useState<ComparisonView>('visual');

  /**
   * Choix manuel de la version de référence, mémorisé **avec la cible à
   * laquelle il se rapporte** : changer de version comparée rétablit ainsi le
   * défaut (« la version qui précède ») sans effet de bord ni second appel.
   */
  const [override, setOverride] = useState<{ target: string; against: string } | null>(null);

  const defaultAgainst = defaultComparisonVersionId(versions, targetVersionId);
  const against =
    override && override.target === targetVersionId ? override.against : defaultAgainst;

  const diff = useAsync<VersionDiffDto | null>(
    () =>
      targetVersionId
        ? getVersionDiff(appId, targetVersionId, against ?? undefined)
        : Promise.resolve(null),
    [appId, targetVersionId, against],
  );

  if (versions.length === 0) {
    return (
      <EmptyState
        title="Rien à comparer pour l'instant"
        description="La comparaison de versions devient possible dès qu'une application a été déployée au moins une fois."
      />
    );
  }

  const data = diff.data;
  const baseSide = data?.base ?? null;
  const targetSide = data?.target ?? null;

  const baseCode = baseSide
    ? source === 'transformed'
      ? baseSide.transformedCode
      : baseSide.originalCode
    : null;
  const targetCode = targetSide
    ? source === 'transformed'
      ? targetSide.transformedCode
      : targetSide.originalCode
    : null;

  // `identical` ne vaut que pour le code transformé : sur le code original, la
  // question se retranche derrière le calcul local.
  const identical = source === 'transformed' ? data?.identical : undefined;
  const firstVersion = data !== null && data.base.id === data.target.id;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor={baseSelectId}>Version de référence</Label>
          <Select
            id={baseSelectId}
            value={against ?? ''}
            onChange={(event) =>
              targetVersionId
                ? setOverride({ target: targetVersionId, against: event.target.value })
                : undefined
            }
          >
            {versions.map((version) => (
              <option key={version.id} value={version.id}>
                {versionLabel(version)}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor={targetSelectId}>Version comparée</Label>
          <Select
            id={targetSelectId}
            value={targetVersionId ?? ''}
            onChange={(event) => onTargetVersionChange(event.target.value)}
          >
            {versions.map((version) => (
              <option key={version.id} value={version.id}>
                {versionLabel(version)}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <ViewSwitch value={view} onChange={setView} />

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {view === 'visual' ? 'Version affichée' : 'Code comparé'}
        </span>
        <Button
          variant={source === 'transformed' ? 'primary' : 'secondary'}
          aria-pressed={source === 'transformed'}
          onClick={() => setSource('transformed')}
        >
          Code transformé
        </Button>
        <Button
          variant={source === 'original' ? 'primary' : 'secondary'}
          aria-pressed={source === 'original'}
          onClick={() => setSource('original')}
        >
          Code original
        </Button>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {source === 'transformed'
            ? 'Le code réellement construit et déployé, après adaptation par l’IA.'
            : 'Le code tel que vous l’avez collé, avant transformation.'}
        </span>
      </div>

      {view === 'visual' && source === 'original' ? (
        <p
          role="status"
          className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300"
        >
          Vous regardez le code d'origine : ce rendu est celui de votre artifact avant adaptation.
          Il peut différer de l'application en ligne — basculez sur « Code transformé » pour voir ce
          qui a réellement été déployé.
        </p>
      ) : null}

      {diff.loading ? (
        <Skeleton className="h-64 w-full" />
      ) : diff.error ? (
        <ErrorBanner message={diff.error} onRetry={diff.reload} />
      ) : !data ? (
        <EmptyState
          title="Sélectionnez une version"
          description="Choisissez la version à comparer dans l'historique ci-dessus."
        />
      ) : (
        <>
          {firstVersion ? (
            <p
              role="status"
              className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300"
            >
              La version v{data.target.versionNumber} est la toute première de cette application :
              aucune version ne la précède, il n'y a donc rien à comparer.
            </p>
          ) : null}

          {source === 'transformed' && (baseCode === null || targetCode === null) ? (
            <p
              role="status"
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200"
            >
              Le code transformé manque d'un côté au moins : la transformation par l'IA n'a pas
              abouti pour cette version. Basculez sur le code original pour la consulter malgré
              tout.
            </p>
          ) : null}

          {view === 'visual' ? (
            <div className="grid gap-4 lg:grid-cols-2">
              <ArtifactPreview code={baseCode} label={`v${data.base.versionNumber}`} />
              <ArtifactPreview code={targetCode} label={`v${data.target.versionNumber}`} />
            </div>
          ) : (
            <CodeDiff
              baseCode={baseCode}
              targetCode={targetCode}
              baseLabel={`v${data.base.versionNumber}`}
              targetLabel={`v${data.target.versionNumber}`}
              identical={identical}
              identicalMessage={
                firstVersion
                  ? 'Première version : elle est comparée à elle-même.'
                  : `Les versions v${data.base.versionNumber} et v${data.target.versionNumber} ont un code rigoureusement identique.`
              }
            />
          )}
        </>
      )}
    </div>
  );
}
